/**
 * @description
 * Pricing configuration component for setting billing models, margins, and subscription tiers per client.
 * Allows configuration of hybrid billing with custom pricing strategies.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-08
 * @see admin/src/frontend/components/stripe/PricingConfiguration.test.tsx
 */

import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { api } from '../../services/api';

interface PricingConfig {
  id: string;
  clientId: string;
  billingModel: 'subscription' | 'usage' | 'hybrid';
  marginPercentage: number | null;
  flatFee: number | null;
  subscriptionTier: string | null;
  customPricing: Record<string, unknown> | null;
  isDefault: boolean;
  usageThreshold: number;
  thresholdAlertAt: number;
  autoChargeOnThreshold: boolean;
}

interface PricingConfigurationProps {
  clientId: string;
}

/**
 * Pricing configuration component
 */
export function PricingConfiguration({ clientId }: PricingConfigurationProps) {
  const [config, setConfig] = useState<PricingConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  // Form state
  const [billingModel, setBillingModel] = useState<'subscription' | 'usage' | 'hybrid'>('hybrid');
  const [marginPercentage, setMarginPercentage] = useState(20);
  const [flatFee, setFlatFee] = useState(0);
  const [subscriptionTier, setSubscriptionTier] = useState('standard');
  const [customPricing, setCustomPricing] = useState('{}');

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const config = await api.getPricingConfig(clientId);
      
      setConfig(config);
      setBillingModel(config.billingModel);
      setMarginPercentage(config.marginPercentage || 20);
      setFlatFee(config.flatFee || 0);
      setSubscriptionTier(config.subscriptionTier || 'standard');
      setCustomPricing(JSON.stringify(config.customPricing || {}, null, 2));
    } catch (error) {
      console.error('Error loading pricing config:', error);
      toast.error('Failed to load pricing configuration');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    try {
      setSaving(true);
      
      let parsedCustomPricing = null;
      try {
        parsedCustomPricing = JSON.parse(customPricing);
      } catch {
        toast.error('Invalid JSON in custom pricing');
        return;
      }

      const newConfig = {
        billingModel,
        marginPercentage,
        flatFee: flatFee * 100, // Convert to cents
        subscriptionTier,
        customPricing: parsedCustomPricing,
      };

      const updatedConfig = await api.updatePricingConfig(clientId, newConfig);
      
      setConfig(updatedConfig);
      setEditing(false);
      toast.success('Pricing configuration saved');
    } catch (error) {
      console.error('Error saving pricing config:', error);
      toast.error('Failed to save pricing configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (config) {
      setBillingModel(config.billingModel);
      setMarginPercentage(config.marginPercentage || 20);
      setFlatFee(config.flatFee || 0);
      setSubscriptionTier(config.subscriptionTier || 'standard');
      setCustomPricing(JSON.stringify(config.customPricing || {}, null, 2));
    }
    setEditing(false);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Pricing Configuration</h3>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm font-medium transition-colors hover:bg-accent"
          >
            Edit
          </button>
        )}
      </div>

      <div className="space-y-4">
        {/* Billing Model */}
        <div>
          <label className="text-sm font-medium">Billing Model</label>
          <p className="text-xs text-muted-foreground mb-2">
            Choose how this client will be billed
          </p>
          {editing ? (
            <select
              value={billingModel}
              onChange={(e) => setBillingModel(e.target.value as 'subscription' | 'usage' | 'hybrid')}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="subscription">Subscription Only</option>
              <option value="usage">Usage Only</option>
              <option value="hybrid">Hybrid (Subscription + Usage)</option>
            </select>
          ) : (
            <div className="rounded-md bg-muted px-3 py-2 text-sm">
              {config?.billingModel === 'subscription' && 'Subscription Only'}
              {config?.billingModel === 'usage' && 'Usage Only'}
              {config?.billingModel === 'hybrid' && 'Hybrid (Subscription + Usage)'}
            </div>
          )}
        </div>

        {/* Margin Percentage */}
        <div>
          <label className="text-sm font-medium">Margin Percentage</label>
          <p className="text-xs text-muted-foreground mb-2">
            Percentage markup on usage costs (e.g., 20 for 20%)
          </p>
          {editing ? (
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                max="100"
                value={marginPercentage}
                onChange={(e) => setMarginPercentage(parseInt(e.target.value) || 0)}
                className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
              <span className="text-sm">%</span>
            </div>
          ) : (
            <div className="rounded-md bg-muted px-3 py-2 text-sm">
              {config?.marginPercentage || 0}%
            </div>
          )}
        </div>

        {/* Flat Fee */}
        <div>
          <label className="text-sm font-medium">Flat Fee</label>
          <p className="text-xs text-muted-foreground mb-2">
            Additional flat fee per billing cycle (in dollars)
          </p>
          {editing ? (
            <div className="flex items-center gap-2">
              <span className="text-sm">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={flatFee}
                onChange={(e) => setFlatFee(parseFloat(e.target.value) || 0)}
                className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
          ) : (
            <div className="rounded-md bg-muted px-3 py-2 text-sm">
              {formatCurrency(config?.flatFee || 0)}
            </div>
          )}
        </div>

        {/* Subscription Tier */}
        <div>
          <label className="text-sm font-medium">Subscription Tier</label>
          <p className="text-xs text-muted-foreground mb-2">
            Base subscription tier for this client
          </p>
          {editing ? (
            <select
              value={subscriptionTier}
              onChange={(e) => setSubscriptionTier(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="basic">Basic</option>
              <option value="standard">Standard</option>
              <option value="premium">Premium</option>
              <option value="enterprise">Enterprise</option>
            </select>
          ) : (
            <div className="rounded-md bg-muted px-3 py-2 text-sm capitalize">
              {config?.subscriptionTier || 'standard'}
            </div>
          )}
        </div>

        {/* Custom Pricing */}
        <div>
          <label className="text-sm font-medium">Custom Pricing (JSON)</label>
          <p className="text-xs text-muted-foreground mb-2">
            Advanced pricing rules in JSON format
          </p>
          {editing ? (
            <textarea
              value={customPricing}
              onChange={(e) => setCustomPricing(e.target.value)}
              rows={4}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
              placeholder='{"anthropic": {"base": 0.01, "per_token": 0.0001}, "vibesdk": {"monthly": 50}}'
            />
          ) : (
            <div className="rounded-md bg-muted px-3 py-2 text-sm font-mono">
              <pre className="whitespace-pre-wrap">
                {JSON.stringify(config?.customPricing || {}, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Preview */}
        <div className="rounded-md bg-blue-50 p-3">
          <h4 className="text-sm font-medium text-blue-900 mb-1">Configuration Preview</h4>
          <div className="text-sm text-blue-800 space-y-1">
            <p>• Billing Model: {billingModel === 'subscription' && 'Subscription Only'}
               {billingModel === 'usage' && 'Usage Only'}
               {billingModel === 'hybrid' && 'Hybrid (Subscription + Usage)'}</p>
            <p>• Margin: {marginPercentage}% on usage costs</p>
            <p>• Flat Fee: {formatCurrency(flatFee * 100)} per cycle</p>
            <p>• Subscription Tier: {subscriptionTier}</p>
            {customPricing !== '{}' && <p>• Custom pricing rules applied</p>}
          </div>
        </div>

        {/* Action Buttons */}
        {editing && (
          <div className="flex gap-2 pt-4">
            <button
              onClick={handleSave}
              disabled={saving}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
            <button
              onClick={handleCancel}
              disabled={saving}
              className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */
